import { RefObject, useState } from 'react'
import { Autocomplete, Box, IconButton, Stack, TextField, useTheme } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { getQuestions, setQuestions } from '../../../../store/actions'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { FsAlertRef } from '../../../common/atoms/Alert'
import { EtlQuestion } from '../../../../shared/model/models'
import { useDebounce } from '../../../../shared/hooks'
import { etlAPI } from '../../../../shared/api/ETLAPI'

const FsQuestion = ({
  question,
  setIsLoading,
  alertRef,
}: {
  question: EtlQuestion
  setIsLoading: (isLoading: boolean) => void
  alertRef: RefObject<FsAlertRef | null>
}) => {
  const { t } = useTranslation()

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { etlQuestions, latestEtlStatus, etlFileCharacteristics } = useAppSelector(
    (state) => state.currentEtl,
  )
  const [uiQuestion, setUIQuestion] = useState<EtlQuestion>(question)

  const options =
    etlFileCharacteristics
      ?.filter((characteristic) => characteristic.isShown)
      .map((characteristic) => characteristic.name) ?? []

  const debouncedUpdateQuestionBE = useDebounce(() => {
    if (etlQuestions) {
      dispatch(setQuestions({ additionalInfo: [...etlQuestions, uiQuestion], isCompleted: false }))
    }
  })

  const deleteQuestion = async (id: number, runId: number) => {
    if (latestEtlStatus) {
      try {
        setIsLoading(true)
        const deleteStatus = await etlAPI.deleteQuestion(id, runId)
        if (deleteStatus === 200) {
          dispatch(getQuestions())
          alertRef.current?.openAlert({ message: t('question.deleted') })
        } else {
          alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
        }
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box
      sx={{ backgroundColor: theme.palette.action.disabledBackground }}
      borderRadius={2}
      p={1}
      maxWidth={800}
    >
      <Stack direction="row" alignItems="flex-start">
        <Stack gap={1}>
          <Stack direction="row" gap={1.5}>
            <TextField
              size="small"
              label={t('type.question')}
              sx={{ width: 490, backgroundColor: theme.palette.background.paper }}
              value={uiQuestion?.question}
              required
              error={uiQuestion?.question === ''}
              onChange={(e) => {
                setUIQuestion({
                  ...uiQuestion,
                  question: e.target.value ?? '',
                })
                debouncedUpdateQuestionBE()
              }}
            />
            <Autocomplete
              size="small"
              sx={{ width: 150, backgroundColor: theme.palette.background.paper }}
              disablePortal
              options={options}
              defaultValue={uiQuestion?.column}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('column')}
                  error={uiQuestion?.column === ''}
                />
              )}
              onChange={(event: any, newValue: string | null) => {
                setUIQuestion({
                  ...uiQuestion,
                  column: newValue ?? '',
                })
                debouncedUpdateQuestionBE()
              }}
            />
            <TextField
              size="small"
              label={t('row')}
              required
              sx={{ width: 80, backgroundColor: theme.palette.background.paper }}
              defaultValue={uiQuestion?.row}
              error={uiQuestion?.row === ''}
              onChange={(e) => {
                setUIQuestion({
                  ...uiQuestion,
                  row: e.target.value ?? '',
                })
                debouncedUpdateQuestionBE()
              }}
            />
          </Stack>
          <Stack width="100%">
            <TextField
              size="medium"
              label={t('type.answer')}
              sx={{ backgroundColor: theme.palette.background.paper }}
              multiline
              defaultValue={uiQuestion?.answer}
              onChange={(e) => {
                setUIQuestion({
                  ...uiQuestion,
                  answer: e.target.value ?? '',
                })
                debouncedUpdateQuestionBE()
              }}
            />
          </Stack>
        </Stack>
        <IconButton
          onClick={() => {
            if (question.id != null && latestEtlStatus?.id !== undefined) {
              deleteQuestion(question.id, latestEtlStatus.id)
            }
          }}
        >
          <DeleteIcon sx={{ '&:hover': { color: theme.palette.error.light } }} />
        </IconButton>
      </Stack>
    </Box>
  )
}

export default FsQuestion
